import {
  aiBuilderStatusSchema,
  aiProposalListSchema,
  assetListSchema,
  customPageSchema,
  customPageSummarySchema,
  managedTenantsSchema,
  publicDashboardSchema,
  publicServerLayoutsSchema,
  publicSiteSchema,
  publishedPageSchema,
  revisionSchema,
  siteStateSchema,
  type PublicSite,
} from '@hearth/shared';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from '@/lib/api';
import { isPreviewMode } from './preview';

/**
 * Query keys: everything a customer reads sits under ['customisation'], everything the studio
 * reads under ['studio', tenant]. Publishing invalidates both.
 */

/** The published site (or the draft in preview mode): branding, theme, login page, nav pages. */
export const publicSiteQuery = queryOptions({
  queryKey: ['customisation', 'site', isPreviewMode()],
  queryFn: ({ signal }): Promise<PublicSite> =>
    apiRequest(`/api/customisation/site${isPreviewMode() ? '?preview=draft' : ''}`, {
      schema: publicSiteSchema,
      signal,
    }),
  staleTime: 60_000,
});

export const publicDashboardQuery = () =>
  queryOptions({
    queryKey: ['customisation', 'dashboard', isPreviewMode()],
    queryFn: ({ signal }) =>
      apiRequest(`/api/customisation/dashboard${isPreviewMode() ? '?preview=draft' : ''}`, {
        schema: publicDashboardSchema,
        signal,
      }),
    staleTime: 60_000,
  });

/** The layouts of every server's overview and console pages (the draft in preview mode). */
export const publicServerLayoutsQuery = () =>
  queryOptions({
    queryKey: ['customisation', 'server-layouts', isPreviewMode()],
    queryFn: ({ signal }) =>
      apiRequest(`/api/customisation/server-layouts${isPreviewMode() ? '?preview=draft' : ''}`, {
        schema: publicServerLayoutsSchema,
        signal,
      }),
    staleTime: 60_000,
  });

export const publicPageQuery = (slug: string) =>
  queryOptions({
    queryKey: ['customisation', 'page', slug, isPreviewMode()],
    queryFn: ({ signal }) =>
      apiRequest(
        `/api/customisation/pages/${encodeURIComponent(slug)}${isPreviewMode() ? '?preview=draft' : ''}`,
        { schema: publishedPageSchema, signal },
      ),
    staleTime: 60_000,
  });

// ---- studio --------------------------------------------------------------------------------

export const managedTenantsQuery = queryOptions({
  queryKey: ['studio', 'tenants'],
  queryFn: ({ signal }) =>
    apiRequest('/api/application/customisation/tenants', { schema: managedTenantsSchema, signal }),
  staleTime: 60_000,
});

export const studioBase = (tenant: string) =>
  `/api/application/customisation/${encodeURIComponent(tenant)}`;

export const siteStateQuery = (tenant: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'site'],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/site`, { schema: siteStateSchema, signal }),
  });

export const siteRevisionsQuery = (tenant: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'site', 'revisions'],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/site/revisions`, {
        schema: z.object({ revisions: z.array(revisionSchema) }),
        signal,
      }),
  });

export const pagesQuery = (tenant: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'pages'],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/pages`, {
        schema: z.object({ pages: z.array(customPageSummarySchema) }),
        signal,
      }),
  });

export const pageQuery = (tenant: string, id: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'pages', id],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/pages/${id}`, { schema: customPageSchema, signal }),
  });

export const pageRevisionsQuery = (tenant: string, id: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'pages', id, 'revisions'],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/pages/${id}/revisions`, {
        schema: z.object({ revisions: z.array(revisionSchema) }),
        signal,
      }),
  });

export const assetsQuery = (tenant: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'assets'],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/assets`, { schema: assetListSchema, signal }),
  });

export const aiProposalsQuery = (tenant: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'ai', 'proposals'],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/ai/proposals`, { schema: aiProposalListSchema, signal }),
  });

export const aiBuilderStatusQuery = (tenant: string) =>
  queryOptions({
    queryKey: ['studio', tenant, 'ai', 'status'],
    queryFn: ({ signal }) =>
      apiRequest(`${studioBase(tenant)}/ai/status`, { schema: aiBuilderStatusSchema, signal }),
    staleTime: 60_000,
  });

/**
 * Upload one image for the tenant. The body is the raw file; the kind and name travel as
 * headers (see docs/CUSTOMISATION.md §5).
 */
export async function uploadAsset(
  tenant: string,
  file: File,
  kind: 'logo' | 'favicon' | 'background' | 'image',
) {
  const { getCsrfToken } = await import('@/lib/api');
  const res = await fetch(`${studioBase(tenant)}/assets`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': file.type,
      'X-Asset-Kind': kind,
      'X-Asset-Name': encodeURIComponent(file.name),
      ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken()! } : {}),
    },
    body: file,
  });
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (json as { error?: { message?: string } } | null)?.error?.message ??
      `Upload failed (${res.status}).`;
    throw new Error(message);
  }
  const { customisationAssetSchema } = await import('@hearth/shared');
  return customisationAssetSchema.parse(json);
}
